<template>
  <div
    v-if="callCenterSelected"
    class="call-center-dashboard"
  >
    <div class="call-center-dashboard__topbar">
      <div>
        <div class="text-sm text-secondary">Call centre dashboard</div>
        <h1 class="mt-1 text-3xl font-semibold md:text-5xl">{{ selectedCallCenterName }}</h1>
      </div>

      <div class="call-center-dashboard__filters">
        <Select
          v-model="callCenterSelected"
          :options="callCentersAllowed"
          value-key="id"
          label-key="name"
          placeholder="Select a Call Center"
          class="min-w-56 shrink-0"
          :readonly="callCentersAllowed.length == 1"
          @select="onCallCenterChange"
        />
        <DatePicker
          v-model="dateRange"
          mode="range"
          :presets="dateRangePresets"
          class="shrink-0"
          @select="onDateRangeChange"
        />
      </div>
    </div>

    <div class="call-center-dashboard__grid">
      <Card
        class="call-center-dashboard__hero"
        no-margin
        :border="false"
        border-radius="2xl"
        pt-classes="call-center-command-card"
      >
        <div>
          <div class="text-sm uppercase tracking-[0.16em] text-white/60">Command centre</div>
          <div class="mt-2 text-3xl font-semibold">Performance snapshot</div>
          <div class="mt-3 text-sm text-white/70">{{ dateRangeLabel }}</div>
        </div>

        <div class="call-center-command-card__metrics">
          <div
            v-for="metric in commandMetrics"
            :key="metric.label"
            class="call-center-command-metric"
          >
            <Icon
              :type="metric.icon"
              class="text-white/70"
            />
            <div class="mt-3 text-2xl font-semibold">{{ metric.value }}</div>
            <div class="text-xs uppercase tracking-[0.14em] text-white/60">{{ metric.label }}</div>
          </div>
        </div>
      </Card>

      <Card
        class="call-center-dashboard__queue"
        :is-loading="callCenterRequest.isLoading.value"
        no-margin
        :border="false"
        border-radius="2xl"
        pt-classes="call-center-card"
      >
        <div class="call-center-card__header">
          <div>
            <div class="text-lg font-semibold">Queue health</div>
            <div class="text-sm text-secondary">Wait and wrap-up pressure</div>
          </div>
        </div>
        <div class="call-center-queue-grid">
          <div
            v-for="metric in queueHealthMetrics"
            :key="metric.label"
            class="call-center-queue-tile"
          >
            <RadialOdometerChart
              v-model="metric.rawValue"
              :label="metric.label"
              :break-points="metric.breakPoints"
              reverse-break-points
              reverse-dial
            >
              {{ metric.value }}
            </RadialOdometerChart>
          </div>
        </div>
      </Card>

      <Card
        class="call-center-dashboard__quality"
        :is-loading="callCenterRequest.isLoading.value"
        no-margin
        :border="false"
        border-radius="2xl"
        pt-classes="call-center-card"
      >
        <div class="call-center-card__header">
          <div>
            <div class="text-lg font-semibold">Conversation quality</div>
            <div class="text-sm text-secondary">Talk and voicemail averages</div>
          </div>
        </div>
        <div class="call-center-quality-list">
          <div
            v-for="metric in conversationMetrics"
            :key="metric.label"
            class="call-center-quality-row"
          >
            <Avatar
              :icon="metric.icon"
              :color="metric.color"
              size="sm"
              no-border
            />
            <div>
              <div class="text-2xl font-semibold">{{ metric.value ?? '00:00' }}</div>
              <div class="text-sm text-secondary">{{ metric.label }}</div>
            </div>
          </div>
        </div>
      </Card>

      <Card
        class="call-center-dashboard__inbound"
        :is-loading="callCenterRequest.isLoading.value"
        no-margin
        :border="false"
        border-radius="2xl"
        pt-classes="call-center-card"
      >
        <div class="call-center-card__header">
          <div>
            <div class="text-lg font-semibold">Inbound calls</div>
            <div class="text-sm text-secondary">{{ formatNumber(totalInboundCalls) }} total</div>
          </div>
        </div>
        <div class="call-center-flow-list">
          <div
            v-for="item in inboundLegend"
            :key="item.label"
            class="call-center-flow-row"
          >
            <div class="call-center-flow-row__top">
              <span>{{ item.label }}</span>
              <strong>{{ item.value }}</strong>
            </div>
            <div class="call-center-flow-row__track">
              <div
                class="call-center-flow-row__fill"
                :style="{ width: percentWidth(item.value, totalInboundCalls), '--flow-color': item.color }"
              ></div>
            </div>
          </div>
        </div>
      </Card>

      <Card
        class="call-center-dashboard__outbound"
        :is-loading="callCenterRequest.isLoading.value"
        no-margin
        :border="false"
        border-radius="2xl"
        pt-classes="call-center-card"
      >
        <div class="call-center-card__header">
          <div>
            <div class="text-lg font-semibold">Outbound calls</div>
            <div class="text-sm text-secondary">{{ formatNumber(totalOutboundCalls) }} total</div>
          </div>
        </div>
        <div class="call-center-flow-list">
          <div
            v-for="item in outboundLegend"
            :key="item.label"
            class="call-center-flow-row"
          >
            <div class="call-center-flow-row__top">
              <span>{{ item.label }}</span>
              <strong>{{ item.value }}</strong>
            </div>
            <div class="call-center-flow-row__track">
              <div
                class="call-center-flow-row__fill"
                :style="{ width: percentWidth(item.value, totalOutboundCalls), '--flow-color': item.color }"
              ></div>
            </div>
          </div>
        </div>
      </Card>

      <Card
        class="call-center-dashboard__tags"
        :is-loading="callCenterRequest.isLoading.value"
        no-margin
        :border="false"
        border-radius="2xl"
        pt-classes="call-center-card"
      >
        <div class="call-center-card__header">
          <div>
            <div class="text-lg font-semibold">Most used tags</div>
            <div class="text-sm text-secondary">{{ callCenterData?.tags?.length ?? 0 }} tags this period</div>
          </div>
        </div>
        <div
          v-if="callCenterData?.tags?.length"
          class="call-center-tag-cloud"
        >
          <div
            v-for="tag in callCenterData.tags"
            :key="tag.tag"
            class="call-center-tag-pill"
          >
            <span class="truncate">{{ tag.tag }}</span>
            <strong>{{ tag.count ?? '' }}</strong>
          </div>
        </div>
        <div
          v-else
          class="call-center-empty-state"
        >
          No tags for this period
        </div>
      </Card>

      <Card
        class="call-center-dashboard__heatmap"
        :is-loading="callCenterRequest.isLoading.value"
        no-margin
        :border="false"
        border-radius="2xl"
        pt-classes="call-center-card"
      >
        <div class="call-center-card__header">
          <div>
            <div class="text-lg font-semibold">Attempted calls</div>
            <div class="text-sm text-secondary">Average hourly volume</div>
          </div>
          <Avatar
            icon="phone"
            color="info"
            size="sm"
            no-border
          />
        </div>
        <apexchart
          v-if="hourlyVolumesComputed.length"
          type="heatmap"
          :options="callsOptions"
          :series="hourlyVolumesComputed"
        />
        <div
          v-else
          class="call-center-empty-state"
        >
          No hourly volume for this period
        </div>
      </Card>

      <Card
        class="call-center-dashboard__trend"
        :is-loading="callCenterRequest.isLoading.value"
        no-margin
        :border="false"
        border-radius="2xl"
        pt-classes="call-center-card"
      >
        <div class="call-center-card__header">
          <div>
            <div class="text-lg font-semibold">Queue trend</div>
            <div class="text-sm text-secondary">Average queue time, last 6 months</div>
          </div>
        </div>
        <apexchart
          v-if="monthlyQueueTimesComputed.series[0]?.data?.length"
          type="area"
          height="190"
          :options="averageQueueTimesOptions"
          :series="monthlyQueueTimesComputed.series"
        />
        <div
          v-else
          class="call-center-empty-state"
        >
          No queue trend for this period
        </div>
      </Card>

      <Card
        class="call-center-dashboard__reasons"
        :is-loading="callCenterRequest.isLoading.value"
        no-margin
        :border="false"
        border-radius="2xl"
        pt-classes="call-center-card"
      >
        <div class="call-center-card__header">
          <div>
            <div class="text-lg font-semibold">Reasons for call</div>
            <div class="text-sm text-secondary">{{ callCenterData?.reasons?.length ?? 0 }} reasons captured</div>
          </div>
        </div>
        <apexchart
          v-if="reasonsForCallComputed.series[0]?.data?.length"
          type="bar"
          height="210"
          :options="reasonsForCallComputed.options"
          :series="reasonsForCallComputed.series"
        />
        <div
          v-else
          class="call-center-empty-state"
        >
          No reasons captured for this period
        </div>
      </Card>
    </div>
  </div>
  <Empty
    v-else-if="!callCentersAllowed.length"
    title="No call centers"
    description="You haven't been added to any call centers"
    icon="headset"
  />
  <Empty
    v-else
    title="No call center selected"
    description="Select a call center"
    icon="headset"
  />
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import Card from '@/components/card/Card.vue'
import Select from '@/components/forms/Select.vue'
import DatePicker from '@/components/date-picker/DatePicker.vue'
import Empty from '@/components/empty/Empty.vue'
import RadialOdometerChart from '@/components/dashboard/charts/radialOdometerChart.vue'
// stores
import { useCallCenterStore } from '@/stores/CallCenterStore'
import { useUserSettingsStore } from '@/stores/UserSettingsStore'
import useApiRequest from '@/composables/useApiRequest'
import dayjs from 'dayjs'
import { alertToast } from '@/utilities/notification'
import { datePresets } from '@/utilities/date-time'
import { formatNumber } from '@/utilities/number'
import Icon from '@/components/icon/Icon.vue'
import Avatar from '@/components/avatar/Avatar.vue'

const callCenterStore = useCallCenterStore()
const userSettingsStore = useUserSettingsStore()

const callCentersAllowed = callCenterStore.callCenters
const callCenterSelected = ref(null)

const callCenterData = ref(null)
const callCenterRequest = useApiRequest()
const callCenterStorageKey = 'report-filter-call-center-selected'
const dateRangeStorageKey = 'call-center-dashboard-date-range'

if (!localStorage.getItem(callCenterStorageKey)) {
  localStorage.setItem(callCenterStorageKey, callCentersAllowed[0]?.id)
  callCenterSelected.value = callCentersAllowed[0]?.id
} else {
  callCenterSelected.value = parseInt(localStorage.getItem(callCenterStorageKey))
}

const dateRange = ref(
  storedDateRange(dateRangeStorageKey, {
    start: dayjs().startOf('month').format('YYYY-MM-DD'),
    end: dayjs().startOf('day').format('YYYY-MM-DD')
  })
)
const dateRangePresets = [datePresets.thisWeek, datePresets.thisMonth, datePresets.lastMonth]

const selectedCallCenterName = computed(() => {
  return callCentersAllowed.find(callCenter => callCenter.id === callCenterSelected.value)?.name ?? 'Call centre'
})

const dateRangeLabel = computed(() => {
  if (!dateRange.value?.start || !dateRange.value?.end) {
    return 'Selected range'
  }

  return `${dayjs(dateRange.value.start).format('Do MMM YYYY')} - ${dayjs(dateRange.value.end).format('Do MMM YYYY')}`
})

const totalInboundCalls = computed(() => {
  return inboundCallsComputed.value.length ? inboundCallsComputed.value.reduce((total, value) => total + value, 0) : 0
})

const totalOutboundCalls = computed(() => {
  return outboundCallsComputed.value.length ? outboundCallsComputed.value.reduce((total, value) => total + value, 0) : 0
})

const commandMetrics = computed(() => [
  {
    label: 'Inbound',
    value: formatNumber(totalInboundCalls.value),
    icon: 'phone-volume'
  },
  {
    label: 'Outbound',
    value: formatNumber(totalOutboundCalls.value),
    icon: 'phone-arrow-up-right'
  },
  {
    label: 'Avg queue',
    value: dayjs.duration(callStatsComputed.value.avgQueueTime ?? 0, 'seconds').format('mm:ss'),
    icon: 'timer'
  },
  {
    label: 'Tags',
    value: formatNumber(callCenterData.value?.tags?.length ?? 0),
    icon: 'tags'
  }
])

const conversationMetrics = computed(() => [
  {
    label: 'Avg voicemail length',
    value: callStatsComputed.value.avgVoicemailLength,
    icon: 'voicemail',
    color: 'brand'
  },
  {
    label: 'Avg talking time',
    value: callStatsComputed.value.avgTalkingTime,
    icon: 'user-clock',
    color: 'info'
  }
])

const queueHealthMetrics = computed(() => [
  {
    label: 'Avg queue time',
    rawValue: callStatsComputed.value.avgQueueTime ?? 0,
    value: dayjs.duration(callStatsComputed.value.avgQueueTime ?? 0, 'seconds').format('mm:ss'),
    breakPoints: [120, 300]
  },
  {
    label: 'Longest queue time',
    rawValue: callStatsComputed.value.longestQueueTime ?? 0,
    value: dayjs.duration(callStatsComputed.value.longestQueueTime ?? 0, 'seconds').format('mm:ss'),
    breakPoints: [120, 300]
  },
  {
    label: 'Avg wrap-up time',
    rawValue: callStatsComputed.value.avgWrapUpTime ?? 0,
    value: dayjs.duration(callStatsComputed.value.avgWrapUpTime ?? 0, 'seconds').format('mm:ss'),
    breakPoints: [120, 240]
  }
])

const inboundLegend = computed(() => {
  const [completed = 0, missed = 0, abandoned = 0, failed = 0] = inboundCallsComputed.value
  return [
    { label: 'Completed', value: completed, color: '#4CA30D' },
    { label: 'Missed', value: missed, color: '#D21C3C' },
    { label: 'Abandoned', value: abandoned, color: '#E89110' },
    { label: 'Failed', value: failed, color: '#475467' }
  ]
})

const outboundLegend = computed(() => {
  const [attempted = 0, opportunity = 0, noAnswer = 0, abandoned = 0, failed = 0] = outboundCallsComputed.value
  return [
    { label: 'Attempted', value: attempted, color: '#4CA30D' },
    { label: 'Opportunity', value: opportunity, color: '#155EEF' },
    { label: 'No Answer', value: noAnswer, color: '#D21C3C' },
    { label: 'Abandoned', value: abandoned, color: '#E89110' },
    { label: 'Failed', value: failed, color: '#475467' }
  ]
})

function onDateRangeChange() {
  localStorage.setItem(dateRangeStorageKey, JSON.stringify(dateRange.value))
  fetchData()
}

function onCallCenterChange() {
  localStorage.setItem(callCenterStorageKey, callCenterSelected.value)
  fetchData()
}

watch(
  dateRange,
  value => {
    localStorage.setItem(dateRangeStorageKey, JSON.stringify(value))
  },
  { deep: true }
)

function storedDateRange(key, fallback) {
  try {
    const value = JSON.parse(localStorage.getItem(key) ?? 'null')

    // New shape: DateRange object with start/end strings
    if (value && typeof value === 'object' && !Array.isArray(value) && value.start && value.end) {
      return value
    }

    // Legacy shape: [start, end] array persisted by the old date picker
    if (Array.isArray(value) && value.length === 2 && value.every(item => typeof item === 'string')) {
      return { start: value[0], end: value[1] }
    }
  } catch {
    return fallback
  }

  return fallback
}

function percentWidth(value, total) {
  return `${Math.max(Math.min(ratioOf(value, total) * 100, 100), value ? 8 : 0)}%`
}

function ratioOf(value, total) {
  if (!total) {
    return 0
  }

  return value / total
}

function fetchData() {
  try {
    callCenterRequest
      .send({
        method: 'GET',
        endpoint: '/v1/dashboards/call-center',
        params: { callCenterId: callCenterSelected.value, startDate: dateRange.value.start, endDate: dateRange.value.end }
      })
      .then(response => {
        if (response.success) {
          callCenterData.value = response.data
        } else {
          alertToast('Failed to fetch call centre data', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to fetch call centre data', err.message, 'error')
  }
}
fetchData()

const inboundCallsComputed = computed(() => {
  if (!callCenterData.value?.inboundCalls.length) {
    return []
  }
  const callsCount = { completed: 0, missed: 0, abandoned: 0, failed: 0 }
  callCenterData.value?.inboundCalls.forEach(day => {
    callsCount.completed += day.completed
    callsCount.missed += day.missed
    callsCount.abandoned += day.abandoned
    callsCount.failed += day.failed
  })

  return Object.keys(callsCount).map(key => callsCount[key])
})

const outboundCallsComputed = computed(() => {
  if (!callCenterData.value?.outboundCalls.length) {
    return []
  }
  const callsCount = { attempted: 0, opportunity: 0, no_answer: 0, abandoned: 0, failed: 0 }
  callCenterData.value?.outboundCalls.forEach(day => {
    callsCount.attempted += day.attempted
    callsCount.opportunity += day.opportunity
    callsCount.no_answer += day.no_answer
    callsCount.abandoned += day.abandoned
    callsCount.failed += day.failed
  })
  return Object.keys(callsCount).map(key => callsCount[key])
})

const callStatsComputed = computed(() => {
  const avgTalkingTime = callCenterData.value?.callStats?.avgTalkingTime
    ? dayjs.duration(callCenterData.value?.callStats?.avgTalkingTime, 'seconds').format('mm:ss')
    : null
  const avgVoicemailLength = callCenterData.value?.callStats?.avgVoicemailLength
    ? dayjs.duration(callCenterData.value?.callStats?.avgVoicemailLength, 'seconds').format('mm:ss')
    : null
  const avgQueueTime = callCenterData.value?.queueStats?.avgQueueTime ?? null
  const longestQueueTime = callCenterData.value?.queueStats?.longestQueueTime ?? null
  const avgWrapUpTime = callCenterData.value?.callStats?.avgWrapUpTime ?? null
  return { avgTalkingTime, avgQueueTime, avgVoicemailLength, longestQueueTime, avgWrapUpTime }
})

const monthlyQueueTimesComputed = computed(() => {
  const queueTimes = callCenterData.value?.monthlyQueueTimes ?? []
  const series = [
    {
      name: 'avg',
      data: queueTimes.map(item => item.avg_queue_time)
    }
  ]
  const months = queueTimes.map(item => item.month)
  const formattedMonths = months?.map(month => dayjs(month).format('MMM')) || []
  return { series, months, formattedMonths }
})

const hourlyVolumesComputed = computed(() => {
  if (!callCenterData.value?.hourlyVolumes) return []

  // helper to get weekday string
  const getWeekday = dateStr => {
    return new Date(dateStr).toLocaleDateString('en-US', { weekday: 'short' })
  }

  const grouped = {}

  callCenterData.value.hourlyVolumes.forEach(day => {
    const weekday = getWeekday(day.date)

    if (!grouped[weekday]) {
      grouped[weekday] = {}
    }

    // only keep hours 9–19 (exclusive of 8 and 20)
    day.hours
      .filter(h => h.hour > 8 && h.hour < 20)
      .forEach(h => {
        if (!grouped[weekday][h.hour]) {
          grouped[weekday][h.hour] = 0
        }
        grouped[weekday][h.hour] += h.count
      })
  })

  // convert to ApexCharts format
  return Object.entries(grouped).map(([weekday, hoursObj]) => ({
    name: weekday,
    data: Object.entries(hoursObj).map(([hour, count]) => ({
      x: hour.toString(),
      y: count
    }))
  }))
})

const reasonsForCallComputed = computed(() => {
  if (!callCenterData.value?.reasons) return { series: [], options: {} }

  const data = callCenterData.value.reasons.map(r => ({
    x: r.reason,
    y: r.count
  }))

  return {
    series: [
      {
        name: 'Count',
        data: data
      }
    ],
    options: {
      chart: {
        sparkline: {
          enabled: false
        },
        parentHeightOffset: 0,
        type: 'bar',
        toolbar: { show: false }
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '80%',
          startingShape: 'rounded',
          endingShape: 'rounded',
          borderRadius: 6
        }
      },
      colors: ['var(--bg-brand-solid)'],
      grid: {
        show: true,
        padding: {
          top: 0,
          left: 0,
          bottom: 0,
          right: 0
        }
      },
      dataLabels: { enabled: false },
      legend: { show: false },
      xaxis: {
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          style: {
            colors: `var(--text-secondary)`
          },
          show: true
        }
      },
      yaxis: {
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          style: {
            colors: `var(--text-secondary)`
          },
          show: true
        }
      }
    }
  }
})

const averageQueueTimesOptions = computed(() => ({
  chart: {
    sparkline: {
      enabled: true
    },
    parentHeightOffset: 0,
    toolbar: { show: false }
  },
  plotOptions: {},
  colors: ['#5bc0de'],
  grid: {
    show: false,
    padding: {
      top: 0,
      left: 0,
      bottom: 0,
      right: 0
    }
  },
  dataLabels: { enabled: false },
  tooltip: {
    custom: function ({ series, dataPointIndex }) {
      const monthValue = monthlyQueueTimesComputed.value.months[dataPointIndex]
      const month = dayjs(monthValue).format('MMM')
      const queueTimeSeconds = series[0][dataPointIndex]
      const formattedTime = dayjs.duration(queueTimeSeconds, 'seconds').format('mm:ss')
      return `
        <div class="px-3 py-2">
          <div class="font-semibold text-sm mb-1">${month}</div>
          <div class="text-xs text-gray-600">Avg. Queue Time: <span class="font-semibold">${formattedTime}</span></div>
        </div>
      `
    }
  },
  legend: { show: false },
  xaxis: {
    categories: monthlyQueueTimesComputed.value.formattedMonths,
    axisBorder: { show: false },
    axisTicks: { show: false },
    labels: {
      style: {
        colors: `var(--text-secondary)`
      },
      show: false
    }
  },
  yaxis: { labels: { show: false } }
}))

/**
 * Gets the effective theme mode for ApexCharts
 * Resolves 'auto' mode by checking system preference or current body class
 * @returns {string} 'light' or 'dark'
 */
const themeMode = computed(() => {
  const theme =
    userSettingsStore.userSettings?.darkmode ||
    JSON.parse(localStorage.getItem('userSettingsLK') ?? '{}')?.darkmode ||
    'light'

  if (theme === 'auto') {
    // Check system preference first, fallback to body class
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark'
    }
    // Fallback to checking if dark mode is currently applied to the body
    return document.body.classList.contains('dark') ? 'dark' : 'light'
  }

  return theme === 'dark' ? 'dark' : 'light'
})

const callsOptions = computed(() => ({
  theme: {
    mode: themeMode.value
  },
  chart: {
    height: 200,
    parentHeightOffset: 0,
    type: 'heatmap',
    toolbar: {
      show: false
    },
    sparkline: {
      enabled: false
    }
  },
  plotOptions: {
    heatmap: {
      useFillColorAsStroke: true,
      radius: 0
    }
  },
  grid: {
    padding: {
      top: -30
    }
  },
  dataLabels: {
    enabled: false
  },
  colors: ['#5bc0de'],
  xaxis: {
    labels: {
      show: false
    },
    axisTicks: { show: false },
    axisBorder: { show: false }
  }
}))
</script>

<style scoped>
.call-center-dashboard {
  display: flex;
  flex-direction: column;
  gap: var(--s-4);
  min-height: calc(100vh - var(--s-8));
}

.call-center-dashboard__topbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--s-4);
}

.call-center-dashboard__filters {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  gap: var(--s-3);
  overflow-x: auto;
  white-space: nowrap;
}

.call-center-dashboard__grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: var(--s-4);
  align-items: start;
}

.call-center-dashboard__hero,
.call-center-dashboard__queue {
  grid-column: span 6;
}

.call-center-dashboard__quality,
.call-center-dashboard__inbound,
.call-center-dashboard__outbound,
.call-center-dashboard__tags {
  grid-column: span 3;
}

.call-center-dashboard__heatmap,
.call-center-dashboard__trend,
.call-center-dashboard__reasons {
  grid-column: span 4;
}

.call-center-dashboard :deep(.card) {
  border-radius: 2rem;
}

:deep(.call-center-command-card),
:deep(.call-center-card) {
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 2rem;
  background: rgba(255, 255, 255, 0.76);
  box-shadow: 0 24px 80px rgba(16, 24, 40, 0.07);
  backdrop-filter: blur(18px);
}

:deep(.call-center-command-card) {
  display: flex;
  flex-direction: column;
  gap: var(--s-5);
  padding: var(--s-5);
  color: white;
  background:
    linear-gradient(180deg, rgba(15, 23, 42, 0.02), rgba(15, 23, 42, 0.58)),
    radial-gradient(circle at 82% 18%, rgba(255, 255, 255, 0.28), transparent 11rem),
    linear-gradient(135deg, #38bdf8, #818cf8 54%, #c084fc);
  background-clip: padding-box;
}

:deep(.call-center-card) {
  height: 100%;
  padding: var(--s-5);
}

.call-center-command-card__metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--s-3);
}

.call-center-command-metric,
.call-center-quality-row,
.call-center-queue-tile,
.call-center-tag-pill {
  border-radius: 1.35rem;
  padding: var(--s-4);
  background: rgba(255, 255, 255, 0.62);
}

.call-center-command-metric {
  background: rgba(255, 255, 255, 0.14);
}

.call-center-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--s-3);
  margin-bottom: var(--s-4);
}

.call-center-queue-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--s-3);
}

.call-center-quality-list,
.call-center-flow-list {
  display: flex;
  flex-direction: column;
  gap: var(--s-3);
}

.call-center-quality-row {
  display: flex;
  align-items: center;
  gap: var(--s-3);
}

.call-center-flow-row {
  display: flex;
  flex-direction: column;
  gap: var(--s-2);
}

.call-center-flow-row__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--s-3);
  font-size: var(--text-sm);
}

.call-center-flow-row__track {
  height: 3rem;
  overflow: hidden;
  border-radius: 1rem;
  background: rgba(148, 163, 184, 0.12);
}

.call-center-flow-row__fill {
  height: 100%;
  border-radius: inherit;
  background:
    repeating-linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.28) 0,
      rgba(255, 255, 255, 0.28) 0.45rem,
      transparent 0.45rem,
      transparent 0.85rem
    ),
    var(--flow-color, #3b82f6);
  box-shadow: 0 16px 34px rgba(59, 130, 246, 0.18);
}

.call-center-tag-cloud {
  display: flex;
  flex-wrap: wrap;
  gap: var(--s-2);
}

.call-center-tag-pill {
  display: inline-flex;
  max-width: 100%;
  align-items: center;
  gap: var(--s-2);
  padding: var(--s-2) var(--s-3);
}

.call-center-tag-pill strong {
  display: inline-flex;
  min-width: 1.8rem;
  justify-content: center;
  border-radius: var(--rounded-full);
  background: rgba(59, 130, 246, 0.12);
  color: var(--utility-blue-700);
  font-size: var(--text-xs);
}

.call-center-empty-state {
  border: 1px dashed var(--border-secondary);
  border-radius: 1.5rem;
  padding: var(--s-5);
  color: var(--text-secondary);
  text-align: center;
}

:global(body.dark .call-center-card),
:global(body.dark .call-center-quality-row),
:global(body.dark .call-center-queue-tile),
:global(body.dark .call-center-tag-pill) {
  border-color: rgba(255, 255, 255, 0.08);
  background: rgba(17, 24, 39, 0.78);
}

:global(body.dark .call-center-command-card) {
  border-color: rgba(255, 255, 255, 0.1);
  background:
    linear-gradient(180deg, rgba(15, 23, 42, 0.04), rgba(15, 23, 42, 0.72)),
    radial-gradient(circle at 78% 18%, rgba(255, 255, 255, 0.12), transparent 11rem),
    linear-gradient(135deg, #075985, #312e81 58%, #581c87);
  background-clip: padding-box;
}

:global(body.dark .call-center-command-metric) {
  background: rgba(255, 255, 255, 0.1);
}

:global(body.dark .call-center-flow-row__track) {
  background: rgba(255, 255, 255, 0.08);
}

@media (max-width: 1280px) {
  .call-center-dashboard__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .call-center-dashboard__hero,
  .call-center-dashboard__queue,
  .call-center-dashboard__heatmap,
  .call-center-dashboard__trend,
  .call-center-dashboard__tags,
  .call-center-dashboard__reasons {
    grid-column: span 2;
  }

  .call-center-dashboard__quality,
  .call-center-dashboard__inbound,
  .call-center-dashboard__outbound {
    grid-column: span 1;
  }
}

@media (max-width: 768px) {
  .call-center-dashboard__topbar {
    flex-direction: column;
    align-items: stretch;
  }

  .call-center-dashboard__grid,
  .call-center-command-card__metrics,
  .call-center-queue-grid {
    grid-template-columns: 1fr;
  }

  .call-center-dashboard__hero,
  .call-center-dashboard__queue,
  .call-center-dashboard__quality,
  .call-center-dashboard__inbound,
  .call-center-dashboard__outbound,
  .call-center-dashboard__tags,
  .call-center-dashboard__heatmap,
  .call-center-dashboard__trend,
  .call-center-dashboard__reasons {
    grid-column: span 1;
  }
}
</style>
